import { ScreenType, ToolType, WeatherType, SaveData, ArtifactInstance, CrewMember, TOOLS } from '../types';
import { DigSite, DigResult } from './DigSite';
import { ArtifactManager } from './Artifact';
import { StoryManager } from './Story';
import { Museum } from './Museum';
import { Economy } from './Economy';
import { CrewManager } from './Crew';
import { Weather } from './Weather';
import { Progression } from './Progression';
import { SITES, getSiteDef } from '../data/sites';
import { getArtifactDef } from '../data/artifacts';
import { MUSEUM_BUYERS } from '../data/museums';
import { RNG } from '../utils/RNG';
import { uid } from '../utils/helpers';

export type GameEvent = {
  type: 'artifact_found' | 'story_fragment' | 'weather_change' | 'day_end' | 'season_end' | 'site_complete' | 'theory_result' | 'sale' | 'grant' | 'media_deal' | 'level_up' | 'site_unlocked' | 'notification';
  data: any;
};

export class Game {
  // Systems
  digSite: DigSite | null = null;
  artifacts: ArtifactManager;
  story: StoryManager;
  museum: Museum;
  economy: Economy;
  crew: CrewManager;
  weather: Weather;
  progression: Progression;
  rng: RNG;

  // State
  playerName: string = 'Dr. Alex Voss';
  currentScreen: ScreenType = 'menu';
  selectedTool: ToolType = 'trowel';
  tutorialComplete: boolean = false;
  eventLog: GameEvent[] = [];

  // Callbacks for UI
  onEvent: ((event: GameEvent) => void) | null = null;

  // Buyer relationships
  buyerRelationships: Map<string, number> = new Map();

  constructor() {
    this.rng = new RNG(Date.now());
    this.artifacts = new ArtifactManager();
    this.story = new StoryManager();
    this.museum = new Museum();
    this.economy = new Economy();
    this.crew = new CrewManager();
    this.weather = new Weather(this.rng);
    this.progression = new Progression();

    // Initialize buyer relationships from static data
    for (const buyer of MUSEUM_BUYERS) {
      this.buyerRelationships.set(buyer.id, buyer.relationship);
    }
  }

  // ── Site Management ──

  startDig(siteId: string): boolean {
    const siteDef = getSiteDef(siteId);
    if (!siteDef) return false;

    if (!this.progression.isSiteUnlocked(siteId)) return false;

    const permitCost = Math.round(siteDef.permitCost * this.progression.getEffectValue('permit_cost'));
    const travelCost = Math.round(siteDef.travelCost * this.progression.getEffectValue('travel_cost'));
    const totalCost = permitCost + travelCost;

    if (!this.economy.canAfford(totalCost)) return false;
    this.economy.spend(totalCost);

    this.digSite = new DigSite(siteDef, this.rng);
    this.weather.setWeather('clear', 0);
    this.currentScreen = 'dig';

    return true;
  }

  leaveSite(): { siteCompleted: boolean } {
    if (!this.digSite) return { siteCompleted: false };

    const siteId = this.digSite.siteDef.id;
    const completion = this.digSite.getCompletionPercent();

    if (completion >= 80) {
      this.emitSiteComplete(completion);
      this.digSite = null;
      this.currentScreen = 'map';
      return { siteCompleted: true };
    }

    this.digSite = null;
    this.currentScreen = 'map';
    return { siteCompleted: false };
  }

  // ── Excavation Actions ──

  digTile(x: number, y: number): DigResult | null {
    if (!this.digSite) return null;
    if (!this.weather.canWork()) return null;

    const weatherMult = this.progression.getEffectValue('weather_immune') >= 1
      ? 1.0
      : this.weather.getTimeMultiplier();
    const speedMod = this.progression.getEffectValue('dig_speed');
    const damageMod = this.progression.getEffectValue('damage_reduce');

    const result = this.digSite.dig(x, y, this.selectedTool, weatherMult, speedMod, damageMod);
    if (!result) return null;

    if (result.artifactFound) {
      this.processArtifactDiscovery(result.artifactFound);
    }

    return result;
  }

  scanArea(x: number, y: number): { x: number; y: number; hasArtifact: boolean }[] | null {
    if (!this.digSite) return null;

    const baseCost = TOOLS.gpr.cost;
    const costMod = this.progression.getEffectValue('gpr_cost');
    const cost = Math.round(baseCost * costMod);

    if (!this.economy.canAfford(cost)) return null;
    this.economy.spend(cost);

    const radius = Math.max(1, Math.floor(this.progression.getEffectValue('gpr_area')));
    return this.digSite.scan(x, y, radius);
  }

  useCharges(x: number, y: number): DigResult | null {
    if (!this.digSite) return null;
    if (!this.weather.canWork()) return null;

    const baseCost = TOOLS.charges.cost;
    const costMod = this.progression.getEffectValue('tool_cost');
    const cost = Math.round(baseCost * costMod);

    if (!this.economy.canAfford(cost)) return null;
    this.economy.spend(cost);

    const damageMod = this.progression.getEffectValue('damage_reduce');
    const result = this.digSite.useCharges(x, y, damageMod);

    if (result.artifactFound) {
      this.processArtifactDiscovery(result.artifactFound);
    }

    return result;
  }

  markTile(x: number, y: number): void {
    if (!this.digSite) return;
    this.digSite.toggleMark(x, y);
  }

  endDay(): void {
    if (!this.digSite) return;

    // Advance weather
    const prevWeather = this.weather.currentWeather;
    const newWeather = this.weather.advanceDay();
    if (newWeather !== prevWeather) {
      this.emitEvent({
        type: 'weather_change',
        data: { previous: prevWeather, current: newWeather, description: this.weather.getDescription() },
      });
    }

    // Pay daily expenses
    const crewWages = this.crew.getDailyWages();
    const museumUpkeep = this.museum.getDailyUpkeep();
    this.economy.payDailyExpenses(crewWages, museumUpkeep);

    // Apply exhibition income
    const getter = (artifactUid: string) => this.artifacts.getByUid(artifactUid);
    const satisfaction = this.museum.getVisitorSatisfaction(getter);
    const displayCount = this.museum.getOccupiedCount();
    if (displayCount > 0) {
      this.economy.applyExhibitionIncome(satisfaction, displayCount);
    }

    // Apply daily grant income from skills
    const dailyIncome = this.progression.getEffectValue('daily_income');
    if (dailyIncome > 0) {
      this.economy.money += dailyIncome;
    }

    // Crew auto-dig on marked tiles
    if (this.weather.canWork() || this.progression.getEffectValue('weather_immune') >= 1) {
      const crewCapacity = this.crew.getDigCapacity();
      if (crewCapacity > 0) {
        const weatherMult = this.progression.getEffectValue('weather_immune') >= 1
          ? 1.0
          : this.weather.getTimeMultiplier();
        const speedMod = this.progression.getEffectValue('dig_speed');
        const damageMod = this.progression.getEffectValue('damage_reduce');

        const crewResults = this.digSite.crewDig(crewCapacity, weatherMult, speedMod, damageMod);
        for (const result of crewResults) {
          if (result.artifactFound) {
            this.processArtifactDiscovery(result.artifactFound);
          }
        }
      }
    }

    // Advance dig site day
    this.digSite.advanceDay();

    this.emitEvent({
      type: 'day_end',
      data: {
        day: this.digSite.currentDay,
        remainingDays: this.digSite.getRemainingDays(),
        money: this.economy.money,
      },
    });

    // Check for season end
    if (this.digSite.isSeasonOver()) {
      const completion = this.digSite.getCompletionPercent();

      if (completion >= 80) {
        this.emitSiteComplete(completion);
      } else {
        this.emitEvent({
          type: 'season_end',
          data: {
            siteId: this.digSite.siteDef.id,
            siteName: this.digSite.siteDef.name,
            completion,
            artifactsFound: this.digSite.artifacts.filter(a => a.discovered).length,
          },
        });
      }
    }
  }

  // ── Analysis Actions ──

  analyzeArtifact(artifactUid: string): { success: boolean; cost: { time: number; money: number } } {
    const artifact = this.artifacts.getByUid(artifactUid);
    if (!artifact) return { success: false, cost: { time: 0, money: 0 } };

    // Check cost before advancing
    const costInfo = this.artifacts.getAnalysisCost(artifactUid);
    if (costInfo.money > 0 && !this.economy.canAfford(costInfo.money)) {
      this.emitEvent({ type: 'notification', data: { text: 'Not enough money.', type: 'error' } });
      return { success: false, cost: costInfo };
    }
    if (this.digSite && costInfo.time > 0 && this.digSite.timeUsedToday + costInfo.time > this.digSite.maxTimePerDay) {
      this.emitEvent({ type: 'notification', data: { text: 'Not enough time left today.', type: 'error' } });
      return { success: false, cost: costInfo };
    }

    const wasDocumented = artifact.analysisState === 'cross_referenced';
    const result = this.artifacts.advanceAnalysis(artifactUid);
    if (!result.success) return result;

    // Pay costs
    if (result.cost.money > 0) {
      this.economy.spend(result.cost.money);
    }
    if (this.digSite && result.cost.time > 0) {
      this.digSite.timeUsedToday += result.cost.time;
    }

    // Check for story fragment on final documentation
    if (wasDocumented && this.artifacts.getByUid(artifactUid)?.analysisState === 'documented') {
      const fragment = this.story.discoverFragment(artifact.defId);
      if (fragment) {
        this.emitEvent({
          type: 'story_fragment',
          data: { fragment, artifactDefId: artifact.defId },
        });
      }
    }

    return result;
  }

  // ── Economy Actions ──

  sellArtifact(artifactUid: string, buyerId: string): number {
    const artifact = this.artifacts.getByUid(artifactUid);
    if (!artifact || artifact.sold) return 0;

    const def = getArtifactDef(artifact.defId);
    if (!def) return 0;

    const buyer = MUSEUM_BUYERS.find(b => b.id === buyerId);
    if (!buyer) return 0;

    const relationship = this.getBuyerRelationship(buyerId);
    const price = this.economy.sellArtifact(artifact, def, buyer, relationship);

    // Update relationship
    const relGainMod = this.progression.getEffectValue('relationship_gain');
    this.updateBuyerRelationship(buyerId, 5 * relGainMod);

    // Mark artifact sold
    this.artifacts.markSold(artifactUid);

    this.emitEvent({
      type: 'sale',
      data: {
        artifactName: this.artifacts.getDisplayName(artifact),
        buyerName: buyer.name,
        price,
      },
    });

    return price;
  }

  getOffers(artifactUid: string): { buyerId: string; buyerName: string; price: number }[] {
    const artifact = this.artifacts.getByUid(artifactUid);
    if (!artifact) return [];

    const def = getArtifactDef(artifact.defId);
    if (!def) return [];

    return MUSEUM_BUYERS.map(buyer => {
      const relationship = this.getBuyerRelationship(buyer.id);
      const price = this.economy.getOffer(artifact, def, buyer, relationship);
      return {
        buyerId: buyer.id,
        buyerName: buyer.name,
        price,
      };
    });
  }

  applyForGrant(): { success: boolean; amount: number } {
    const result = this.economy.applyForGrant(this.progression.reputation, this.rng);

    if (result.success) {
      this.emitEvent({
        type: 'grant',
        data: { amount: result.amount },
      });
    }

    return result;
  }

  // ── Museum Actions ──

  placeInMuseum(artifactUid: string, slotIndex: number): boolean {
    const artifact = this.artifacts.getByUid(artifactUid);
    if (!artifact || artifact.sold || artifact.displayed) return false;

    const placed = this.museum.placeArtifact(artifactUid, slotIndex);
    if (!placed) return false;

    this.artifacts.markDisplayed(artifactUid, slotIndex);
    return true;
  }

  removeFromMuseum(slotIndex: number): boolean {
    const removedUid = this.museum.removeArtifact(slotIndex);
    if (!removedUid) return false;

    this.artifacts.removeFromDisplay(removedUid);
    return true;
  }

  buyMuseumUpgrade(upgrade: string): boolean {
    const cost = this.museum.getUpgradeCost(upgrade);
    if (cost <= 0) return false;
    if (!this.economy.canAfford(cost)) return false;

    const success = this.museum.purchaseUpgrade(upgrade);
    if (!success) return false;

    this.economy.spend(cost);
    return true;
  }

  expandMuseum(): boolean {
    const cost = this.museum.getExpansionCost();
    if (!this.economy.canAfford(cost)) return false;

    this.economy.spend(cost);
    this.museum.expandSlots(3);
    return true;
  }

  // ── Story/Theory Actions ──

  submitTheory(theoryId: string, chosenIndex: number): { correct: boolean; repChange: number; loreText: string | null } {
    const result = this.story.submitTheory(theoryId, chosenIndex);

    if (result.correct) {
      const rewardMod = this.progression.getEffectValue('theory_reward');
      const repGain = Math.round(result.repChange * rewardMod);
      this.progression.addReputation(repGain);
      this.progression.recordTheory(true);

      const skillPointsAwarded = this.progression.checkSkillPointAwards();
      if (skillPointsAwarded > 0) {
        this.emitEvent({
          type: 'level_up',
          data: { skillPoints: skillPointsAwarded, totalSkillPoints: this.progression.skillPoints },
        });
      }

      // Check if new sites unlocked
      this.checkSiteUnlocks();

      this.emitEvent({
        type: 'theory_result',
        data: { correct: true, repChange: repGain, loreText: result.loreText },
      });

      return { correct: true, repChange: repGain, loreText: result.loreText };
    } else {
      const noPenalty = this.progression.getEffectValue('theory_no_penalty') >= 1;
      const repLoss = noPenalty ? 0 : Math.abs(result.repChange);
      if (repLoss > 0) {
        this.progression.loseReputation(repLoss);
      }
      this.progression.recordTheory(false);

      this.emitEvent({
        type: 'theory_result',
        data: { correct: false, repChange: -repLoss, loreText: null },
      });

      return { correct: false, repChange: -repLoss, loreText: null };
    }
  }

  // ── Crew Actions ──

  getHireCandidates(): CrewMember[] {
    return this.crew.generateCandidates(this.rng);
  }

  hireCrew(member: CrewMember): boolean {
    // Check if we can afford the first day's wage as a sanity check
    this.crew.hire(member);
    return true;
  }

  fireCrew(memberId: string): void {
    this.crew.fire(memberId);
  }

  // ── Skill Actions ──

  unlockSkill(skillId: string): boolean {
    const check = this.progression.canUnlockSkill(skillId);
    if (!check.canUnlock) return false;

    const previousTier = this.progression.getTier();
    const success = this.progression.unlockSkill(skillId);
    if (!success) return false;

    const newTier = this.progression.getTier();
    if (newTier.tier !== previousTier.tier) {
      this.emitEvent({
        type: 'level_up',
        data: { newTier: newTier.label },
      });
    }

    return true;
  }

  // ── Navigation ──

  setScreen(screen: ScreenType): void {
    this.currentScreen = screen;
  }

  // ── Save/Load ──

  exportSave(): SaveData {
    const discoveredFragments: string[] = Array.from(this.story.discoveredFragments.keys());

    const submittedTheories: { theoryId: string; correct: boolean }[] = [];
    for (const [theoryId, correct] of this.story.submittedTheories) {
      submittedTheories.push({ theoryId, correct });
    }

    const buyerRelationships: { buyerId: string; relationship: number }[] = [];
    for (const [buyerId, relationship] of this.buyerRelationships) {
      buyerRelationships.push({ buyerId, relationship });
    }

    return {
      version: 1,
      playerName: this.playerName,
      money: this.economy.money,
      reputation: this.progression.reputation,
      currentSiteId: this.digSite ? this.digSite.siteDef.id : null,
      currentDay: this.digSite ? this.digSite.currentDay : 0,
      artifacts: this.artifacts.inventory.map(a => ({ ...a })),
      discoveredFragments,
      submittedTheories,
      skills: Array.from(this.progression.unlockedSkills),
      skillPoints: this.progression.skillPoints,
      museumSlots: this.museum.slots.map(s => ({ ...s })),
      museumUpgrades: Array.from(this.museum.upgrades),
      buyerRelationships,
      crew: this.crew.crew.map(c => ({ ...c })),
      sitesCompleted: Array.from(this.progression.completedSites),
      siteCompletionStars: Object.fromEntries(this.progression.siteCompletionStars),
      totalArtifactsFound: this.progression.totalArtifactsFound,
      totalTheoriesCorrect: this.progression.totalTheoriesCorrect,
      tutorialComplete: this.tutorialComplete,
      currentWeather: this.weather.currentWeather,
      weatherDaysLeft: this.weather.daysLeft,
      siteGridState: this.digSite ? this.digSite.grid.map(row => row.map(tile => ({ ...tile, layers: tile.layers.map(l => ({ ...l })) }))) : undefined,
      siteArtifacts: this.digSite ? this.digSite.artifacts.map(a => ({ ...a })) : undefined,
      timestamp: Date.now(),
    };
  }

  importSave(data: SaveData): void {
    // Restore player state
    this.playerName = data.playerName;
    this.tutorialComplete = data.tutorialComplete;

    // Restore economy
    this.economy = new Economy();
    this.economy.money = data.money;

    // Restore progression
    this.progression = new Progression();
    this.progression.reputation = data.reputation;
    this.progression.skillPoints = data.skillPoints;
    this.progression.totalArtifactsFound = data.totalArtifactsFound;
    this.progression.totalTheoriesCorrect = data.totalTheoriesCorrect;
    for (const skillId of data.skills) {
      this.progression.unlockedSkills.add(skillId);
    }
    for (const siteId of data.sitesCompleted) {
      this.progression.completedSites.add(siteId);
    }
    if (data.siteCompletionStars) {
      for (const [siteId, stars] of Object.entries(data.siteCompletionStars)) {
        this.progression.siteCompletionStars.set(siteId, stars);
      }
    }

    // Restore artifacts
    this.artifacts = new ArtifactManager();
    for (const a of data.artifacts) {
      this.artifacts.inventory.push({ ...a });
    }

    // Restore story by re-discovering fragments from artifacts
    this.story = new StoryManager();
    for (const a of data.artifacts) {
      if (a.discovered) {
        this.story.discoverFragment(a.defId);
      }
    }
    for (const t of data.submittedTheories) {
      this.story.submittedTheories.set(t.theoryId, t.correct);
    }

    // Restore museum
    this.museum = new Museum();
    // Replace slots with saved slots
    this.museum.slots = data.museumSlots.map(s => ({ ...s }));
    for (const upgrade of data.museumUpgrades) {
      this.museum.upgrades.add(upgrade);
    }

    // Restore crew
    this.crew = new CrewManager();
    for (const c of data.crew) {
      this.crew.crew.push({ ...c });
    }

    // Restore weather
    this.weather = new Weather(this.rng);
    this.weather.setWeather(data.currentWeather, data.weatherDaysLeft);

    // Restore buyer relationships
    this.buyerRelationships.clear();
    for (const br of data.buyerRelationships) {
      this.buyerRelationships.set(br.buyerId, br.relationship);
    }

    // Restore dig site if there was an active one
    if (data.currentSiteId && data.siteGridState) {
      const siteDef = getSiteDef(data.currentSiteId);
      if (siteDef) {
        this.digSite = new DigSite(siteDef, this.rng);
        // Overwrite the generated grid with saved grid state
        this.digSite.grid = data.siteGridState.map(row =>
          row.map(tile => ({
            ...tile,
            layers: tile.layers.map(l => ({ ...l })),
          }))
        );
        this.digSite.currentDay = data.currentDay;

        // Restore artifact instances on the dig site.
        // Start with discovered artifacts from the save data.
        this.digSite.artifacts = data.artifacts
          .filter(a => a.siteId === data.currentSiteId)
          .map(a => ({ ...a }));

        // Also restore undiscovered artifacts that are still on the grid.
        // These aren't in the save's artifacts array, so we rebuild stub instances
        // from the grid tile data so that dig() can discover them.
        const savedUids = new Set(this.digSite.artifacts.map(a => a.uid));
        const size = siteDef.gridSize;
        for (let y = 0; y < size; y++) {
          for (let x = 0; x < size; x++) {
            const tile = this.digSite.grid[y][x];
            if (tile.artifactId && !savedUids.has(tile.artifactId)) {
              // This is an undiscovered artifact still embedded in the grid.
              // We don't know its defId from the save, so we also need to save it.
              // For now, check if we saved siteArtifacts; otherwise use a stub.
              const siteArtifact = data.siteArtifacts?.find((a: any) => a.uid === tile.artifactId);
              if (siteArtifact) {
                this.digSite.artifacts.push({ ...siteArtifact });
              }
            }
          }
        }
      }
    } else {
      this.digSite = null;
    }

    // Reset event log
    this.eventLog = [];
  }

  // ── Helpers ──

  private emitEvent(event: GameEvent): void {
    this.eventLog.push(event);
    if (this.onEvent) {
      this.onEvent(event);
    }
  }

  private processArtifactDiscovery(artifact: ArtifactInstance): void {
    // Add to artifact manager
    console.log('[DIG DEEP] processArtifactDiscovery:', { defId: artifact.defId, discovered: artifact.discovered, siteId: artifact.siteId, uid: artifact.uid });
    this.artifacts.addArtifact(artifact);

    // Record in progression
    this.progression.recordArtifact();

    // Emit artifact found event
    const def = getArtifactDef(artifact.defId);
    this.emitEvent({
      type: 'artifact_found',
      data: {
        artifact,
        name: def ? def.name : 'Unknown Artifact',
        rarity: def ? def.rarity : 'common',
        condition: artifact.condition,
      },
    });

    // Check for media deal on rare+ finds
    if (def) {
      const rareOrAbove = new Set(['rare', 'legendary', 'mythical']);
      if (rareOrAbove.has(def.rarity)) {
        const mediaDeal = this.economy.getMediaDeal(def.rarity, this.progression.reputation);
        this.emitEvent({
          type: 'media_deal',
          data: { amount: mediaDeal, artifactName: def.name, rarity: def.rarity },
        });
      }
    }

    // Check skill point awards
    const skillPointsAwarded = this.progression.checkSkillPointAwards();
    if (skillPointsAwarded > 0) {
      this.emitEvent({
        type: 'level_up',
        data: { skillPoints: skillPointsAwarded, totalSkillPoints: this.progression.skillPoints },
      });
    }

    // Reputation boost for finding artifacts
    if (def) {
      const repGain = def.rarity === 'mythical' ? 5
        : def.rarity === 'legendary' ? 3
        : def.rarity === 'rare' ? 2
        : def.rarity === 'uncommon' ? 1
        : 0;
      if (repGain > 0) {
        this.progression.addReputation(repGain);
        this.checkSiteUnlocks();
      }
    }
  }

  private getBuyerRelationship(buyerId: string): number {
    return this.buyerRelationships.get(buyerId) ?? 0;
  }

  private updateBuyerRelationship(buyerId: string, amount: number): void {
    const current = this.getBuyerRelationship(buyerId);
    this.buyerRelationships.set(buyerId, Math.min(100, current + amount));
  }

  private emitSiteComplete(completion: number): void {
    if (!this.digSite) return;

    const site = this.digSite;
    const siteId = site.siteDef.id;
    const discovered = site.artifacts.filter(a => a.discovered);
    const totalArtifacts = site.artifacts.length;
    const rareFinds = discovered.filter(a => {
      const def = getArtifactDef(a.defId);
      return def && ['rare', 'legendary', 'mythical'].includes(def.rarity);
    }).length;

    // Count theories submitted for this site
    const siteTheories = this.story.getTheoriesForSite(siteId);
    const theoriesSubmitted = siteTheories.filter(t => this.story.submittedTheories.has(t.id)).length;

    const repBefore = this.progression.reputation;
    const { repBonus, stars } = this.progression.completeSite(siteId, completion);
    const repAfter = this.progression.reputation;

    // Check for newly unlocked sites
    const newlyUnlocked: string[] = [];
    for (const s of SITES) {
      if (s.repRequired > repBefore && s.repRequired <= repAfter) {
        newlyUnlocked.push(s.name);
      }
    }

    this.emitEvent({
      type: 'site_complete',
      data: {
        siteId,
        siteName: site.siteDef.name,
        completion: Math.round(completion),
        artifactsFound: discovered.length,
        totalArtifacts,
        rareFinds,
        moneyEarned: this.economy.money, // current money as proxy
        daysUsed: site.currentDay,
        totalDays: site.siteDef.seasonDays,
        theoriesSubmitted,
        repBonus,
        stars,
        newlyUnlocked,
      },
    });
  }

  private checkSiteUnlocks(): void {
    const unlockedSites = this.progression.getUnlockedSites();
    for (const site of unlockedSites) {
      // Emit unlock event for sites that were just unlocked
      // We track this by checking if the site is unlocked but not yet completed and not the starting site
      if (site.repRequired > 0 && !this.progression.completedSites.has(site.id)) {
        // Only emit if reputation just crossed the threshold (within 5 points)
        if (this.progression.reputation >= site.repRequired && this.progression.reputation < site.repRequired + 5) {
          this.emitEvent({
            type: 'site_unlocked',
            data: { siteId: site.id, siteName: site.name, location: site.location },
          });
        }
      }
    }
  }
}
